import React, { useState, useEffect } from "react";
import { authFetch } from "../pages/Auth";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  COLORS, CHART_COLORS, commonAxisStyle, commonGridStyle,
  tooltipStyle, cardStyle, chartHeaderStyle,
} from "../chartTheme";

function useApi(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch(path)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);
  return { data, loading, error };
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        border: `3px solid ${COLORS.border}`,
        borderTopColor: COLORS.primary,
        animation: "spin 0.8s linear infinite",
      }} />
    </div>
  );
}

function KPICard({ label, value, sub, color, icon }) {
  return (
    <div style={{ ...cardStyle, position: "relative", overflow: "hidden" }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: color || COLORS.primary, borderRadius: "10px 10px 0 0",
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase" }}>{label}</span>
        {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || COLORS.text, lineHeight: 1, marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.textDim }}>{sub}</div>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <div style={chartHeaderStyle}>{title}</div>
      {children}
    </div>
  );
}

export default function InitiationPhase() {
  const summary = useApi("/api/analytics/summary");
  const successRates = useApi("/api/prior-auth/success-rates");
  const payers = useApi("/api/prior-auth/payers");

  const isLoading = summary.loading || successRates.loading || payers.loading;
  if (isLoading) return <Spinner />;

  const s = summary.data || {};
  const sr = successRates.data || {};
  const payerList = Array.isArray(payers.data) ? payers.data : (payers.data?.payers || []);

  // Derive KPIs
  const totalEnrolled = s.total_patients || s.totalPatients || 0;
  const activePatients = s.active_patients || s.activePatients || 0;
  const approvalRate = sr.overall_approval_rate || sr.approval_rate || null;
  const avgTimeToFill = sr.avg_time_to_first_fill || null;

  // Enrollment trend from summary
  const enrollmentTrend = s.enrollment_trend || s.enrollmentTrend || [];

  // PA by payer
  const payerData = payerList.slice(0, 8).map((p) => ({
    name: p.payer_name || p.name || "Unknown",
    rate: Math.round((p.approval_rate || p.approvalRate || 0) * 100) / 100,
  }));

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
        <KPICard label="Total Enrolled" value={totalEnrolled.toLocaleString()} sub="All-time enrollments" color={COLORS.primary} icon="📊" />
        <KPICard label="Active Patients" value={activePatients.toLocaleString()} sub="Currently in program" color={COLORS.success} icon="✓" />
        <KPICard
          label="PA Approval Rate"
          value={approvalRate != null ? `${approvalRate}%` : "N/A"}
          sub="Prior authorization success"
          color={COLORS.info}
          icon="📋"
        />
        {avgTimeToFill != null && (
          <KPICard label="Avg Time to First Fill" value={`${avgTimeToFill}d`} sub="Days from enrollment" color={COLORS.warning} icon="⏱" />
        )}
      </div>

      {/* Enrollment Trends */}
      <SectionCard title="Enrollment Trends">
        {enrollmentTrend.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={enrollmentTrend}>
              <CartesianGrid {...commonGridStyle} />
              <XAxis dataKey="month" tick={commonAxisStyle} />
              <YAxis tick={commonAxisStyle} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="count" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 30 }}>
            Enrollment trend data unavailable
          </div>
        )}
      </SectionCard>

      {/* PA Intelligence */}
      <SectionCard title="Prior Authorization — Approval by Payer">
        {payerData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={payerData} layout="vertical">
              <CartesianGrid {...commonGridStyle} />
              <XAxis type="number" tick={commonAxisStyle} domain={[0, 100]} unit="%" />
              <YAxis type="category" dataKey="name" tick={commonAxisStyle} width={120} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="rate" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 30 }}>
            Payer data unavailable — configure prior auth tracking to populate
          </div>
        )}
      </SectionCard>

      {/* Candidate Identification */}
      <SectionCard title="Candidate Identification">
        <div style={{ color: COLORS.textDim, fontSize: 12, lineHeight: 1.8 }}>
          <p style={{ margin: "0 0 8px" }}>
            Patients meeting GLP-1 eligibility criteria (BMI ≥ 30 or BMI ≥ 27 with comorbidities) who are not yet enrolled
            can be identified via the <strong style={{ color: COLORS.text }}>Patients</strong> tab using risk and comorbidity filters.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            <div style={{ background: COLORS.bgDark, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Type 2 Diabetes</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{s.comorbidity_diabetes || s.diabetes_count || "—"}</div>
            </div>
            <div style={{ background: COLORS.bgDark, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Hypertension</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{s.comorbidity_hypertension || s.hypertension_count || "—"}</div>
            </div>
            <div style={{ background: COLORS.bgDark, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Cardiovascular</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{s.comorbidity_cardiovascular || s.cardiovascular_count || "—"}</div>
            </div>
            <div style={{ background: COLORS.bgDark, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Sleep Apnea</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{s.comorbidity_sleep_apnea || s.sleep_apnea_count || "—"}</div>
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
